
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Edit, Trash2, Eye, DollarSign, Paperclip, MoreHorizontal, Building, Phone, User } from "lucide-react";
import { Client } from "@/types/client";
import { useClientStatusOptions } from "@/hooks/useClientStatusOptions";
import { ClientProfilePopover } from "./ClientProfilePopover";

import { toast } from "sonner";

interface ClientsCardsViewProps {
  clients: Client[];
  onEditClient: (client: Client) => void;
  onDeleteClient: (clientId: string) => void;
  onShowFiles?: (client: Client) => void;
}

export const ClientsCardsView = ({ clients, onEditClient, onDeleteClient, onShowFiles }: ClientsCardsViewProps) => {
  const { getStatusStyle, getStatusLabel } = useClientStatusOptions();

  return (
    <>
      {clients.map((client) => (
        <Card key={client.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-start">
              <div className="space-y-1">
                <h3 className="font-semibold text-lg">{client.name_ar}</h3>
                {client.name_en && (
                  <p className="text-sm text-muted-foreground">{client.name_en}</p>
                )}
              </div>
                <Badge 
                  className="text-white"
                  style={getStatusStyle(client.status)}
                >
                  {getStatusLabel(client.status)}
                </Badge>
              </div>
              
              <div className="space-y-2">
                {client.company && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building className="w-4 h-4 text-muted-foreground" />
                    <span>{client.company}</span>
                  </div>
                )}
                
                {client.mobile && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span dir="ltr">{client.mobile}</span>
                  </div>
                )}
                
                {((client.creator && 'name' in client.creator) || client.employees?.name) && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>أنشأه: {(client.creator && 'name' in client.creator ? client.creator.name : null) || client.employees?.name}</span>
                  </div>
                )}
                
                {/* معلومات إضافية */}
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">تاريخ الإنشاء: {new Date(client.created_at).toLocaleDateString('ar-SA')}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-2">
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => onEditClient(client)}
                    variant="outline"
                  >
                    <Edit className="w-4 h-4 ml-1" />
                    تعديل
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive" 
                    onClick={() => onDeleteClient(client.id)}
                  >
                    <Trash2 className="w-4 h-4 ml-1" />
                    حذف
                  </Button>
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <ClientProfilePopover clientId={client.id}>
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                        <Eye className="mr-2 h-4 w-4" />
                        <span>عرض ملف العميل</span>
                      </DropdownMenuItem>
                    </ClientProfilePopover>
                    <DropdownMenuItem onClick={() => {
                      toast.info("قريباً - عرض الحالة المالية للعميل");
                    }}>
                      <DollarSign className="mr-2 h-4 w-4" />
                      <span>الحالة المالية</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onShowFiles?.(client)}>
                      <Paperclip className="mr-2 h-4 w-4" />
                      <span>المستمسكات ({client.attachments?.length || 0})</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  );
};
